import { Component, OnDestroy, OnInit } from '@angular/core';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DividerModule } from 'primeng/divider';
import { AccountSwitchService, AccountSwitchEvent } from '../../services/account-switch/account-switch.service';
import { DataService } from '../../services/data/data.service';
import { Subject, takeUntil } from 'rxjs';
import { Texts } from '../../services/language/texts';

@Component({
  selector: 'app-account-switch-dialog',
  standalone: true,
  imports: [DividerModule],
  templateUrl: './account-switch-dialog.component.html',
  styleUrls: ['./account-switch-dialog.component.css'],
})
export class AccountSwitchDialogComponent implements OnInit, OnDestroy {
  currentAccount: { email: string; tenantId: string } | null = null;
  event: AccountSwitchEvent | null = null;
  isLoading = false;
  texts: Texts = {} as Texts;
  private destroy$ = new Subject<void>();

  constructor(
    private accountSwitchService: AccountSwitchService,
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private dataService: DataService,
  ) {
    this.texts = this.dataService.getTexts();
  }

  ngOnInit(): void {
    this.currentAccount = this.accountSwitchService.getCurrentAccountInfo();
    this.event = this.config.data?.event || null;
    this.texts = this.dataService.getTexts();
    this.dataService.languageChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.texts = this.dataService.getTexts();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTitle(): string {
    switch (this.event?.type) {
      case 'user_not_registered':
        return this.texts.AccountSwitchTitleUserNotRegistered;
      case 'tenant_mismatch':
        return this.texts.AccountSwitchTitleTenantMismatch;
      case 'access_denied':
        return this.texts.AccountSwitchTitleAccessDenied;
      default:
        return this.texts.AccountSwitchTitleDefault;
    }
  }

  getMessage(): string {
    if (this.event?.message) {
      return this.event.message;
    }

    switch (this.event?.type) {
      case 'user_not_registered':
        return this.texts.AccountSwitchMessageUserNotRegistered;
      case 'tenant_mismatch':
        return this.texts.AccountSwitchMessageTenantMismatch;
      case 'access_denied':
        return this.texts.AccountSwitchMessageAccessDenied;
      default:
        return this.texts.AccountSwitchMessageDefault;
    }
  }

  async onSwitchAccount(): Promise<void> {
    this.isLoading = true;
    try {
      // Triggers a redirect to Microsoft login - page will navigate away on success.
      // Error handling covers edge cases: popup blockers, MSAL config errors, or network failures.
      await this.accountSwitchService.loginWithAccountPicker();
    } catch (error) {
      console.error('Account switch failed:', error);
      this.isLoading = false;
    }
  }

  async onFreshLogin(): Promise<void> {
    this.isLoading = true;
    try {
      // Triggers a redirect to Microsoft login - page will navigate away on success.
      // Error handling covers edge cases: popup blockers, MSAL config errors, or network failures.
      await this.accountSwitchService.loginWithFreshCredentials();
    } catch (error) {
      console.error('Fresh login failed:', error);
      this.isLoading = false;
    }
  }

  async onLogoutAndRestart(): Promise<void> {
    this.isLoading = true;
    try {
      // Triggers a redirect to Microsoft logout - page will navigate away on success.
      // Error handling covers edge cases: popup blockers, MSAL config errors, or network failures.
      await this.accountSwitchService.logoutAndRelogin();
    } catch (error) {
      console.error('Logout and restart failed:', error);
      this.isLoading = false;
    }
  }

  onSelectTenant(): void {
    this.isLoading = true;
    this.dialogRef.close({ action: 'select_tenant' });
  }
}
